import type { RadarAxis } from '../lib/types';
import { clamp } from '../lib/format';
import styles from './SentimentRadar.module.css';

interface Props {
  axes: RadarAxis[];
  size?: number;
  className?: string;
}

const RING_COUNT = 4;

export function SentimentRadar({ axes, size = 320, className }: Props) {
  if (!axes.length) return null;
  const cx = size / 2;
  const cy = size / 2;
  const r = size / 2 - 32;
  const step = (Math.PI * 2) / axes.length;

  const point = (axisIdx: number, value: number) => {
    const angle = -Math.PI / 2 + step * axisIdx;
    const radius = clamp(value, 0, 1) * r;
    return {
      x: cx + Math.cos(angle) * radius,
      y: cy + Math.sin(angle) * radius,
    };
  };

  const polygon = axes
    .map((a, i) => {
      const p = point(i, a.value);
      return `${p.x.toFixed(2)},${p.y.toFixed(2)}`;
    })
    .join(' ');

  return (
    <svg
      className={[styles.svg, className].filter(Boolean).join(' ')}
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      role="img"
      aria-label="Sentiment radar across five linguistic dimensions"
    >
      {/* Concentric rings */}
      {Array.from({ length: RING_COUNT }, (_, i) => {
        const ringR = (r * (i + 1)) / RING_COUNT;
        return (
          <circle
            key={i}
            cx={cx}
            cy={cy}
            r={ringR}
            fill="none"
            stroke="var(--tia-grey-200)"
            strokeWidth={1}
          />
        );
      })}
      {/* Axis spokes */}
      {axes.map((a, i) => {
        const p = point(i, 1);
        return (
          <line
            key={a.label}
            x1={cx}
            y1={cy}
            x2={p.x}
            y2={p.y}
            stroke="var(--tia-grey-200)"
            strokeWidth={1}
          />
        );
      })}
      {/* Filled value polygon */}
      <polygon
        points={polygon}
        fill="var(--tia-navy)"
        fillOpacity={0.18}
        stroke="var(--tia-navy)"
        strokeWidth={1.5}
        strokeLinejoin="round"
      />
      {/* Vertices */}
      {axes.map((a, i) => {
        const p = point(i, a.value);
        return (
          <circle
            key={`v-${a.label}`}
            cx={p.x}
            cy={p.y}
            r={3}
            fill="var(--tia-navy)"
          />
        );
      })}
      {/* Axis labels */}
      {axes.map((a, i) => {
        const p = point(i, 1.18);
        return (
          <text
            key={`l-${a.label}`}
            x={p.x}
            y={p.y}
            textAnchor="middle"
            dominantBaseline="middle"
            className={styles.axisLabel}
          >
            {a.label}
          </text>
        );
      })}
    </svg>
  );
}
